import { ArrowForward, Delete, Save, SwapHoriz } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Relationship } from '@/lib/types';

type RelationshipDescriptionFieldsProps = {
  sourceName: string;
  targetName: string;
  forwardDescription: string;
  reverseDescription: string;
  onForwardDescriptionChange: (value: string) => void;
  onReverseDescriptionChange: (value: string) => void;
  disabled?: boolean;
};

export function RelationshipDescriptionFields({
  sourceName,
  targetName,
  forwardDescription,
  reverseDescription,
  onForwardDescriptionChange,
  onReverseDescriptionChange,
  disabled = false,
}: RelationshipDescriptionFieldsProps) {
  const { t } = useTranslation();

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {t('relationships.forward_relationship')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
          <Typography variant="body2" fontWeight={600}>{sourceName}</Typography>
          <ArrowForward sx={{ fontSize: 16 }} />
          <Typography variant="body2" fontWeight={600}>{targetName}</Typography>
        </Box>
        <TextField
          fullWidth
          size="small"
          value={forwardDescription}
          onChange={(event) => onForwardDescriptionChange(event.target.value)}
          disabled={disabled}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {t('relationships.reverse_relationship_optional')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
          <Typography variant="body2" fontWeight={600}>{targetName}</Typography>
          <ArrowForward sx={{ fontSize: 16 }} />
          <Typography variant="body2" fontWeight={600}>{sourceName}</Typography>
        </Box>
        <TextField
          fullWidth
          size="small"
          value={reverseDescription}
          onChange={(event) => onReverseDescriptionChange(event.target.value)}
          disabled={disabled}
        />
      </Box>
    </Stack>
  );
}

type RelationshipEditorDialogProps = {
  open: boolean;
  relationship: Relationship | null;
  sourceName: string;
  targetName: string;
  isManaged?: boolean;
  isSaving?: boolean;
  showConnectorStyle?: boolean;
  onClose: () => void;
  onSave: (relationship: Relationship) => void | Promise<void>;
  onRequestDelete: (relationship: Relationship) => void;
};

export function RelationshipEditorDialog({
  open,
  relationship,
  sourceName,
  targetName,
  isManaged = false,
  isSaving = false,
  showConnectorStyle = false,
  onClose,
  onSave,
  onRequestDelete,
}: RelationshipEditorDialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Relationship | null>(relationship);
  const [isReversed, setIsReversed] = useState(false);

  useEffect(() => {
    setDraft(relationship ? { ...relationship } : null);
    setIsReversed(false);
  }, [relationship]);

  if (!draft || !relationship) return null;

  const draftSourceName = isReversed ? targetName : sourceName;
  const draftTargetName = isReversed ? sourceName : targetName;

  const handleReverseDirection = () => {
    setDraft((current) => {
      if (!current) return current;

      const reverseDescription = current.reverseDescription?.trim();
      const next: Relationship = {
        ...current,
        entityAId: current.entityBId,
        entityAType: current.entityBType,
        entityBId: current.entityAId,
        entityBType: current.entityAType,
        forwardDescription: reverseDescription || current.forwardDescription,
      };

      if (reverseDescription) {
        next.reverseDescription = current.forwardDescription;
      } else {
        delete next.reverseDescription;
      }

      return next;
    });
    setIsReversed((current) => !current);
  };

  const handleSave = () => {
    const forwardDescription = draft.forwardDescription.trim();
    if (!forwardDescription || isManaged) return;

    const updated = {
      ...draft,
      forwardDescription,
    };
    const reverseDescription = draft.reverseDescription?.trim();
    if (reverseDescription) {
      updated.reverseDescription = reverseDescription;
    } else {
      delete updated.reverseDescription;
    }

    void onSave(updated);
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('relationships.edit_relationship')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {isManaged && (
            <Alert severity="info">
              <Typography variant="subtitle2" fontWeight={700}>
                {t('relationships.managed_relationship_title')}
              </Typography>
              <Typography variant="body2">
                {t('relationships.managed_relationship_description')}
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            {showConnectorStyle ? (
              <FormControlLabel
                control={(
                  <Switch
                    checked={(draft.connectorStyle || 'straight') === 'orthogonal'}
                    onChange={(event) => setDraft((current) => (
                      current
                        ? { ...current, connectorStyle: event.target.checked ? 'orthogonal' : 'straight' }
                        : current
                    ))}
                    disabled={isManaged || isSaving}
                  />
                )}
                label={t('relationships.connector_style_orthogonal')}
                sx={{ m: 0 }}
              />
            ) : <Box />}
            <Button
              variant="outlined"
              startIcon={<SwapHoriz />}
              onClick={handleReverseDirection}
              disabled={isManaged || isSaving}
            >
              {t('relationships.reverse_direction')}
            </Button>
          </Box>

          <RelationshipDescriptionFields
            sourceName={draftSourceName}
            targetName={draftTargetName}
            forwardDescription={draft.forwardDescription}
            reverseDescription={draft.reverseDescription || ''}
            onForwardDescriptionChange={(value) => setDraft((current) => (
              current ? { ...current, forwardDescription: value } : current
            ))}
            onReverseDescriptionChange={(value) => setDraft((current) => (
              current ? { ...current, reverseDescription: value } : current
            ))}
            disabled={isManaged || isSaving}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button
          color="error"
          startIcon={<Delete />}
          onClick={() => onRequestDelete(relationship)}
          disabled={isManaged || isSaving}
        >
          {t('relationships.delete_relationship')}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={isSaving} color="inherit">
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={isManaged || isSaving || !draft.forwardDescription.trim()}
          >
            {t('common.save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
